import './FileSubmissionBoxStyle.less';

import { Icon } from '@opendoor/bricks';
import React from 'react';

import values from 'lodash/values';

import {
  BINSR_TYPE,
  humanizedRepairPreferences,
  REPAIR_PREFERENCES,
} from '../../constants/RepairPreferenceConstants';
import { RepairPreference } from '../../declarations';
import { humanizeDocumentType } from '../../helpers/formatUtils';

export interface IFileSubmissionBoxProps {
  documentTypes: string[];
  file: File;
  onCancel: () => void;
  onSave: (name: string, documentType: string) => void;
  onSaveRepairPreferences?: (
    repairPreferenceNotes: string | null,
    sellerResponsePreference: RepairPreference,
  ) => void;
}

export interface IFileSubmissionBoxState {
  documentType: string;
  errors: {
    documentTypeError: boolean;
    nameError: boolean;
    repairPreferenceNotesError: boolean;
    sellerResponsePreferenceError: boolean;
  };
  name: string;
  repairPreferenceNotes: string;
  sellerResponsePreference: string;
}

export default class FileSubmissionBox extends React.Component<
  IFileSubmissionBoxProps,
  IFileSubmissionBoxState
> {
  public state: IFileSubmissionBoxState = {
    documentType: this.props.documentTypes.length === 1 ? this.props.documentTypes[0] : '',
    errors: {
      documentTypeError: false,
      nameError: false,
      repairPreferenceNotesError: false,
      sellerResponsePreferenceError: false,
    },
    name: this.props.file.name,
    repairPreferenceNotes: '',
    sellerResponsePreference: '',
  };

  public handleDocumentTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    this.setState({ documentType: event.target.value });

  public handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({ name: event.target.value });

  public handleRepairPreferenceNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    this.setState({ repairPreferenceNotes: event.target.value });

  public handleSellerResponsePreferenceChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    this.setState({ sellerResponsePreference: event.target.value });

  public handleSave = () => this.validateForm(this.saveForm);

  public saveForm = () => {
    const { file, onSave, onSaveRepairPreferences } = this.props;
    const {
      documentType,
      errors,
      name,
      repairPreferenceNotes,
      sellerResponsePreference,
    } = this.state;

    if (values(errors).some(error => error)) {
      return;
    }

    const trimmedName = name.trim();
    const splitOriginalName = file.name.split('.');
    const extension = splitOriginalName[splitOriginalName.length - 1];
    onSave(
      trimmedName.endsWith(extension) ? trimmedName : `${trimmedName}.${extension}`,
      documentType,
    );
    if (documentType === BINSR_TYPE && onSaveRepairPreferences) {
      onSaveRepairPreferences(
        repairPreferenceNotes || null,
        sellerResponsePreference as RepairPreference,
      );
    }
  };

  public validateForm = (callback?: () => void) => {
    const { documentType, name, repairPreferenceNotes, sellerResponsePreference } = this.state;

    const documentTypeError = !documentType;

    const nameError = !name.trim();

    let repairPreferenceNotesError = false;
    let sellerResponsePreferenceError = false;
    if (documentType === BINSR_TYPE) {
      sellerResponsePreferenceError = !sellerResponsePreference;
      if (sellerResponsePreference !== REPAIR_PREFERENCES.none_requested) {
        repairPreferenceNotesError = !repairPreferenceNotes.trim();
      }
    }

    this.setState(
      {
        errors: {
          documentTypeError,
          nameError,
          repairPreferenceNotesError,
          sellerResponsePreferenceError,
        },
      },
      callback,
    );
  };

  public renderEditNameRow = () => {
    const { name, errors } = this.state;

    return (
      <div className="edit-name-row">
        <div className="name-label">Rename Document</div>
        <input
          className={errors.nameError ? 'error' : ''}
          onChange={this.handleNameChange}
          type="text"
          value={name}
        />
      </div>
    );
  };

  public renderEditDocumentTypeRow = () => {
    const { documentTypes } = this.props;
    const { documentType, errors } = this.state;
    const singleDocumentType = documentTypes.length === 1;

    return (
      <div className="edit-document-type-row">
        <div className="type-label">Document Type</div>
        <select
          className={errors.documentTypeError ? 'error' : ''}
          disabled={singleDocumentType}
          onChange={this.handleDocumentTypeChange}
        >
          {singleDocumentType ? (
            <option value={documentType}>{humanizeDocumentType(documentType)}</option>
          ) : (
            [
              <option key="" value="">
                Select document type
              </option>,
            ].concat(
              documentTypes.map(documentTypeOption => (
                <option key={documentTypeOption} value={documentTypeOption}>
                  {humanizeDocumentType(documentTypeOption)}
                </option>
              )),
            )
          )}
        </select>
      </div>
    );
  };

  public renderSellerResponsePreferenceRow = () => {
    const { errors } = this.state;

    return (
      <div className="repair-preferences-row">
        <div className="repair-preference-label">
          What type of response would your buyer prefer?
        </div>
        <div>
          <select
            className={errors.sellerResponsePreferenceError ? 'error' : ''}
            onChange={this.handleSellerResponsePreferenceChange}
          >
            {[
              <option key="" value="">
                Select preference
              </option>,
            ].concat(
              Object.keys(REPAIR_PREFERENCES).map(repairPreferenceKey => (
                <option key={repairPreferenceKey} value={REPAIR_PREFERENCES[repairPreferenceKey]}>
                  {humanizedRepairPreferences[repairPreferenceKey]}
                </option>
              )),
            )}
          </select>
        </div>
      </div>
    );
  };

  public renderRepairPreferenceNotesRow = () => {
    const { errors, repairPreferenceNotes } = this.state;

    return (
      <div className="repair-preference-notes-row">
        <div className="repair-preference-notes-label">
          Which repair items are most important to your buyer?
        </div>
        <div>
          <textarea
            className={errors.repairPreferenceNotesError ? 'error' : ''}
            onChange={this.handleRepairPreferenceNotesChange}
            placeholder="Please list the repair items that are most important to your client. e.g. the roof"
            value={repairPreferenceNotes}
          />
        </div>
      </div>
    );
  };

  public render(): JSX.Element {
    const { onCancel } = this.props;
    const { documentType } = this.state;

    const showRepairPreferenceForm = documentType === BINSR_TYPE;

    return (
      <div className="resale-closing-dashboard-file-submission-box">
        <div className="input-container row">
          <div className="file-icon col-lg-1 col-md-1">
            <Icon name="file-pdf" />
          </div>
          <div className="inner-input-container col-lg-8 col-md-11">
            {this.renderEditNameRow()}
            {this.renderEditDocumentTypeRow()}
            {showRepairPreferenceForm && (
              <div className="repair-preference-form">
                {this.renderSellerResponsePreferenceRow()}
                {this.renderRepairPreferenceNotesRow()}
              </div>
            )}
          </div>
        </div>
        <div className="button-container">
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button className="save-button" onClick={this.handleSave}>
            Save
          </button>
        </div>
        <br className="clear" />
      </div>
    );
  }
}
